import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { RotateCcw } from "lucide-react";
import UploadButton, { fileUrl } from "@/components/UploadButton";

const SECTIONS = [
  {
    key: "hero",
    title: "Homepage Hero",
    description: "The first thing visitors see on the home page.",
    fields: [
      { name: "kicker", label: "Kicker (small caps above headline)", type: "input" },
      { name: "headline_pre", label: "Headline — first part", type: "input" },
      { name: "headline_highlight", label: "Headline — highlighted word (orange)", type: "input" },
      { name: "headline_post", label: "Headline — last part", type: "input" },
      { name: "subtext", label: "Sub-text paragraph", type: "textarea" },
      { name: "image", label: "Hero background image URL", type: "image" },
      { name: "cta_primary", label: "Primary button text", type: "input" },
      { name: "cta_secondary", label: "Secondary button text", type: "input" },
    ],
  },
  {
    key: "categories",
    title: "Category Pages",
    description: "Hero image, headline and intro text for each category landing page (also used as the card image on the home page).",
    fields: [
      { name: "chopping_image", label: "Chopping Boards — image", type: "image" },
      { name: "chopping_kicker", label: "Chopping Boards — kicker", type: "input" },
      { name: "chopping_heading", label: "Chopping Boards — heading", type: "input" },
      { name: "chopping_intro", label: "Chopping Boards — intro paragraph", type: "textarea" },
      { name: "resin_image", label: "Resin Platters — image", type: "image" },
      { name: "resin_kicker", label: "Resin Platters — kicker", type: "input" },
      { name: "resin_heading", label: "Resin Platters — heading", type: "input" },
      { name: "resin_intro", label: "Resin Platters — intro paragraph", type: "textarea" },
      { name: "furniture_image", label: "Furniture — image", type: "image" },
      { name: "furniture_kicker", label: "Furniture — kicker", type: "input" },
      { name: "furniture_heading", label: "Furniture — heading", type: "input" },
      { name: "furniture_intro", label: "Furniture — intro paragraph", type: "textarea" },
      { name: "miscellaneous_image", label: "Miscellaneous — image", type: "image" },
      { name: "miscellaneous_kicker", label: "Miscellaneous — kicker", type: "input" },
      { name: "miscellaneous_heading", label: "Miscellaneous — heading", type: "input" },
      { name: "miscellaneous_intro", label: "Miscellaneous — intro paragraph", type: "textarea" },
    ],
  },
  {
    key: "home_about",
    title: "Home Page — 'About Me' Section (The Maker block)",
    description: "The artisan/about block on the HOME page (between featured products and workshops banner). Distinct from the /about page.",
    fields: [
      { name: "kicker", label: "Kicker", type: "input" },
      { name: "heading", label: "Heading", type: "input" },
      { name: "paragraph1", label: "Paragraph 1", type: "textarea" },
      { name: "paragraph2", label: "Paragraph 2", type: "textarea" },
      { name: "image", label: "Image URL", type: "image" },
    ],
  },
  {
    key: "featured",
    title: "Home Page — Featured Products Heading",
    description: "Heading above the featured products grid on the home page.",
    fields: [
      { name: "kicker", label: "Kicker (e.g. 'Selected Pieces')", type: "input" },
      { name: "heading", label: "Heading (e.g. 'From the Workshop')", type: "input" },
    ],
  },
  {
    key: "about_page",
    title: "About Page",
    description: "The dedicated /about page content.",
    fields: [
      { name: "kicker", label: "Kicker", type: "input" },
      { name: "heading", label: "Heading", type: "input" },
      { name: "paragraph1", label: "Paragraph 1", type: "textarea" },
      { name: "paragraph2", label: "Paragraph 2", type: "textarea" },
      { name: "paragraph3", label: "Paragraph 3", type: "textarea" },
      { name: "signature", label: "Signature line", type: "input" },
      { name: "image", label: "Side image URL", type: "image" },
      { name: "stat1_value", label: "Stat 1 — value", type: "input" },
      { name: "stat1_label", label: "Stat 1 — label", type: "input" },
      { name: "stat2_value", label: "Stat 2 — value", type: "input" },
      { name: "stat2_label", label: "Stat 2 — label", type: "input" },
      { name: "stat3_value", label: "Stat 3 — value", type: "input" },
      { name: "stat3_label", label: "Stat 3 — label", type: "input" },
    ],
  },
  {
    key: "workshops",
    title: "Workshops Page & Home Banner",
    description: "Content for the resin-workshop waitlist page and the matching banner on the home page.",
    fields: [
      { name: "kicker", label: "Kicker (e.g. 'Coming soon')", type: "input" },
      { name: "heading", label: "Heading", type: "input" },
      { name: "subtext", label: "Sub-text paragraph", type: "textarea" },
      { name: "image", label: "Workshop image URL", type: "image" },
    ],
  },
  {
    key: "footer",
    title: "Footer",
    description: "Tagline shown next to the brand in the site footer.",
    fields: [
      { name: "tagline", label: "Tagline", type: "textarea" },
    ],
  },
];

export default function AdminContent() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content && Object.keys(content).length) setDraft(content);
  }, [content]);

  const set = (section, field, value) => {
    setDraft((d) => ({ ...d, [section]: { ...(d[section] || {}), [field]: value } }));
  };

  const saveSection = async (sectionKey) => {
    setSaving(true);
    try {
      await api.put("/content", { [sectionKey]: draft[sectionKey] });
      await refresh();
      toast.success("Saved");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!confirm("Reset ALL site content to the original defaults? This can't be undone.")) return;
    setSaving(true);
    try {
      await api.post("/admin/content/reset");
      await refresh();
      toast.success("Reset to defaults");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-content-page" className="max-w-4xl">
      <div className="flex items-start justify-between mb-10 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl text-[#F5F5DC]">Site Content</h1>
          <p className="text-[#A3A3A3] text-sm mt-2 max-w-xl">
            Edit headings, paragraphs and images that appear on the home page, about page and footer. Changes are live the moment you hit save.
          </p>
        </div>
        <button onClick={resetAll} className="btn-ghost text-xs uppercase tracking-widest flex items-center gap-2" data-testid="reset-content-btn">
          <RotateCcw size={14} /> Reset all to defaults
        </button>
      </div>

      <div className="space-y-8">
        {SECTIONS.map((section) => {
          const data = draft[section.key] || {};
          return (
            <div key={section.key} className="bg-[#111] border border-[#2A2A2A] p-6" data-testid={`section-${section.key}`}>
              <div className="border-b border-[#2A2A2A] pb-4 mb-6">
                <h2 className="font-display text-2xl text-[#F5F5DC]">{section.title}</h2>
                <p className="text-[#A3A3A3] text-sm mt-1">{section.description}</p>
              </div>

              <div className="space-y-5">
                {section.fields.map((f) => (
                  <div key={f.name}>
                    <label className="smallcaps block mb-2">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={3}
                        className="input-dark"
                        value={data[f.name] ?? ""}
                        onChange={(e) => set(section.key, f.name, e.target.value)}
                        data-testid={`${section.key}-${f.name}`}
                      />
                    ) : f.type === "image" ? (
                      <div className="grid sm:grid-cols-[1fr_120px] gap-4 items-start">
                        <div className="space-y-2">
                          <input
                            className="input-dark"
                            placeholder="https://… or upload below"
                            value={data[f.name] ?? ""}
                            onChange={(e) => set(section.key, f.name, e.target.value)}
                            data-testid={`${section.key}-${f.name}`}
                          />
                          <UploadButton
                            kind="image"
                            onUploaded={(url) => set(section.key, f.name, url)}
                          />
                        </div>
                        {data[f.name] && (
                          <img src={fileUrl(data[f.name])} alt="" className="w-full h-24 object-cover border border-[#2A2A2A]" onError={(e) => { e.target.style.display = "none"; }} />
                        )}
                      </div>
                    ) : (
                      <input
                        className="input-dark"
                        value={data[f.name] ?? ""}
                        onChange={(e) => set(section.key, f.name, e.target.value)}
                        data-testid={`${section.key}-${f.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-[#2A2A2A] flex justify-end">
                <button
                  onClick={() => saveSection(section.key)}
                  disabled={saving}
                  className="btn-primary"
                  data-testid={`save-${section.key}`}
                >
                  {saving ? "Saving…" : `Save ${section.title}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
